import { BeneficiarySummary } from "@beneficiary/domain/models/beneficiary-summary.model";
import { GetAllBeneficiariesUseCase } from "@beneficiary/domain/usecases/get-all-beneficiaries.usecase";
import { Derivation } from "@catalogue/domain/models/derivation.model";
import { Service } from "@catalogue/domain/models/service.model";
import { CreateDerivationUseCase } from "@catalogue/domain/usecases/create-derivation.usecase";
import { GetAllDerivationsUseCase } from "@catalogue/domain/usecases/get-all-derivations.usecase";
import { GetAllServicesUseCase } from "@catalogue/domain/usecases/get-all-services.usecase";
import { GetDerivationByIdUseCase } from "@catalogue/domain/usecases/get-derivation-by-id.usecase";
import { UpdateDerivationUseCase } from "@catalogue/domain/usecases/update-derivation.usecase";
import type { DerivationFormValues } from "@catalogue/presentation/components/derivation/edit-derivation-form.component";
import { GetLoggedUserUseCase } from "@authentication/domain/usecases/get-logged-user.usecase";
import { UserSessionRepository } from "@authentication/data/repositories/user-session.repository";
import { GlobalEntity } from "@entity/domain/models/global-entity.model";
import { GetAllGlobalEntitiesUseCase } from "@entity/domain/usecases/get-all-global-entities.usecase";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { inject, injectable } from "inversify";
import { makeObservable, observable, action, runInAction } from "mobx";
import { IncDocument } from "@core/domain/models/inc-document.model";
import { StatusEnumDto } from "@catalogue/data/dto/status-enum.dto";
import { UserSearchFilters } from "@user/domain/models/user.model";
import { UserRoleEnum } from "@user/domain/models/user-role.model";
import { SearchUsersByUseCase } from "@user/domain/usecases/search-users-by.usecase";
import { UserSummary } from "@user/domain/models/users-summary.model";
import { Pagination } from "@core/domain/models/pagination";

@injectable()
export class DerivationEditPageViewModel extends BaseViewModel {
    @observable currentDerivation: Derivation | null = null;
    @observable beneficiaries: BeneficiarySummary[] = [];
    @observable services: Service[] = [];
    @observable entities: GlobalEntity[] = [];
    @observable loading: boolean = false;
    @observable servicesByEntity: Service[] = [];
    @observable entityAdmins: UserSummary[] = [];

    constructor(
        @inject(GetDerivationByIdUseCase)
        private readonly getDerivationByIdUseCase: GetDerivationByIdUseCase,
        @inject(UpdateDerivationUseCase)
        private readonly updateDerivationUseCase: UpdateDerivationUseCase,
        @inject(GetAllBeneficiariesUseCase)
        private readonly getAllBeneficiariesUseCase: GetAllBeneficiariesUseCase,
        @inject(GetAllServicesUseCase)
        private readonly getAllServicesUseCase: GetAllServicesUseCase,
        @inject(GetAllGlobalEntitiesUseCase)
        private readonly getAllGlobalEntitiesUseCase: GetAllGlobalEntitiesUseCase,
        @inject(GetLoggedUserUseCase)
        private readonly getLoggedUserUseCase: GetLoggedUserUseCase,
        @inject(SearchUsersByUseCase)
        private readonly searchUsersByUseCase: SearchUsersByUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    async fetchDerivation(derivationId: number): Promise<void> {
        this.loading = true;
        try {
            const derivation = await this.getDerivationByIdUseCase.execute(derivationId);
            runInAction(() => {
                this.currentDerivation = derivation;
                if (derivation && derivation.entityId) {
                    this.fetchServicesByEntity(derivation.entityId);
                }
            });
        } catch (error) {
            console.error("Error fetching derivation:", error);
            ToastManagerStore.error("Error al cargar la derivación");
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    @action
    async fetchBeneficiaries(): Promise<void> {
        try {
            const beneficiaries = await this.getAllBeneficiariesUseCase.execute();
            runInAction(() => {
                this.beneficiaries = beneficiaries;
            });
        } catch (error) {
            console.error("Error fetching beneficiaries:", error);
        }
    }

    @action
    async fetchEntities(): Promise<void> {
        try {
            const entities = await this.getAllGlobalEntitiesUseCase.execute();
            runInAction(() => {
                this.entities = entities;
            });
        } catch (error) {
            console.error("Error fetching entities:", error);
        }
    }

    @action
    async fetchServicesByEntity(entityId: number): Promise<void> {
        try {
            // Filtra servicios por entidad
            const services = await this.getAllServicesUseCase.execute({ entityId });
            runInAction(() => {
                this.servicesByEntity = services.items;
            });
        } catch (error) {
            console.error("Error fetching services by entity:", error);
        }
    }

    @action
    async updateDerivation(derivationId: number, values: DerivationFormValues): Promise<boolean> {
        const user = this.getLoggedUserUseCase.execute();

        if (!user.entityId) {
            ToastManagerStore.error("No se puede actualizar la derivación: El usuario actual no tiene una entidad asignada.");
            return false;
        }

        LoadLayoutStore.start();
        let success = false;
        try {
            const updateData = {
                id: derivationId,
                beneficiaryId: values.beneficiaryId,
                serviceId: values.serviceId,
                entityId: values.entityId,
                assignedById: user.entityId,
                status: values.status || StatusEnumDto.Requested,
                reason: values.reason ?? null,
                documentIds: values.documents?.documents?.map((doc: IncDocument) => doc.id) || [],
                authorizationFileId: values.documents?.documents?.[0]?.id ?? null,
                notify_admin_id: values.notify_admin_id ?? 0,
            };

            const updatedDerivation = await this.updateDerivationUseCase.execute(updateData);
            success = !!updatedDerivation;
            if (success) {
                ToastManagerStore.success("Derivación actualizada correctamente");
            }
        } catch (error) {
            console.error("Error updating derivation:", error);
            ToastManagerStore.error("Error al actualizar la derivación");
        } finally {
            LoadLayoutStore.finish();
        }
        return success;
    }

    @action
    async fetchEntityAdminsByEntity(entityId: number): Promise<void> {
        try {
            // Definir filtros para buscar sólo administradores de la entidad especificada
            const filters: UserSearchFilters = {
                userRole: UserRoleEnum.EntityAdmin,
                entityIds: [entityId.toString()],
                active: true,  // Opcional: filtrar solo usuarios activos
                fromCatalogue: true // Nuevo filtro para el flujo catalogue
            };

            // Usar el caso de uso y el repository, siguiendo el patrón de la app
            const users = await this.searchUsersByUseCase.execute(
                Pagination.NoPagination(),
                filters
            );

            runInAction(() => {
                this.entityAdmins = users.usersSummary;
            });
        } catch (error) {
            console.error('Error fetching entity admins:', error);
            ToastManagerStore.error('No se pudieron cargar los administradores de la entidad');
        }
    }
}